import React, { useState } from 'react';

// material-ui
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip
} from '@mui/material';

// project imports
import IconButton from 'components/@extended/IconButton';

// assets
import { DeleteFilled } from '@ant-design/icons';
import 'react-widgets/scss/styles.scss';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { errorMessage, nameRegExp } from 'utils/constants';
import { MODAL_ACTION_TYPE } from 'utils/constants/modal';
import CountryCodeSelect from '../../components/common/CountryCodeSelect';

// ==============================|| USER ADD / EDIT / DELETE ||============================== //

const UpdateUserModal = ({
    onClose,
    type,
    onCreateOrUpdate,
    roles,
    onDelete,
    user,
    loading
}) => {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .trim()
            .lowercase()
            .required(errorMessage.firstName.required)
            .matches(nameRegExp.format, errorMessage.firstName.valid),
        last_name: Yup.string()
            .trim()
            .lowercase()
            .required(errorMessage.lastName.required)
            .matches(nameRegExp.format, errorMessage.lastName.valid),
        phone: Yup.object().shape({
            number: Yup.string().trim().required(errorMessage.phone.required)
        }),
        email: Yup.string()
            .trim()
            .lowercase()
            .nullable()
            .notRequired()
            .email(errorMessage.email.valid),
        type: Yup.string(),
        primary: Yup.boolean(),
        primary_manager: Yup.boolean()
    });

    const validatePhoneNumber = async values => {
        const { number, code, country } = values.phone || {};
        const phoneSchema = Yup.string().phone(country);

        if (!number) {
            return { 'phone.number': errorMessage.phone.required };
        }

        try {
            phoneSchema.validateSync(`${code}${number}`);
            return {};
        } catch (error) {
            return {
                'phone.number': 'Phone number needs to be valid'
            };
        }
    };

    const handleDelete = () => {
        onDelete(user);
        onClose();
    };

    const submitForm = formValues => {
        const managerData = {
            ...formValues,
            first_name: formValues.first_name.toLowerCase(),
            last_name: formValues.last_name.toLowerCase(),
            company_name: formValues.company_name ? formValues.company_name.toLowerCase() : null,
            phone: formValues?.phone?.number
                ? {
                      ...formValues.phone,
                      number: Number(formValues.phone.number)
                  }
                : null,
            primaryManager: formValues.primaryManager
        };

        onCreateOrUpdate(managerData);
        onClose();
    };

    const getUserRoleKey = () => {
        if (!user?.type) return roles[0]?.key || '';

        if (roles.some(role => role.key === user.type)) {
            return user.type;
        }

        const foundRole = roles.find(role => role.name === user.type);
        return foundRole ? foundRole.key : roles[0]?.key || '';
    };

    return (
        <>
            <DialogTitle>
                {type === MODAL_ACTION_TYPE.ADD ? 'Create User' : 'Edit User'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={{
                    first_name: user?.first_name || '',
                    last_name: user?.last_name || '',
                    phone: {
                        code: user?.phone?.code || 1,
                        country: user?.phone?.country || 'US',
                        number: user?.phone?.number || ''
                    },
                    email: user?.email || '',
                    company_name: user?.company_name || '',
                    type: getUserRoleKey(),
                    primary: user?.primary || false,
                    //TODO: Remove this once we have custom role for this user type
                    primary_manager: false
                }}
                onSubmit={submitForm}
                validationSchema={validationSchema}
                validateOnChange
                validate={validatePhoneNumber}
            >
                {({
                    handleSubmit,
                    handleChange,
                    touched,
                    setFieldValue,
                    handleBlur,
                    errors,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, minHeight: 360 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Manager First Name</InputLabel>
                                        <TextField
                                            required
                                            id="firstNameBasic"
                                            name="first_name"
                                            placeholder="First Name"
                                            fullWidth
                                            autoComplete="given-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.first_name && !!errors.first_name
                                            }
                                            helperText={
                                                touched.first_name && errors.first_name
                                            }
                                            value={values.first_name}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Manager Last Name</InputLabel>
                                        <TextField
                                            required
                                            id="lastNameBasic"
                                            name="last_name"
                                            placeholder="Last name"
                                            fullWidth
                                            autoComplete="family-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.last_name && !!errors.last_name
                                            }
                                            helperText={
                                                touched.last_name && errors.last_name
                                            }
                                            value={values.last_name}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Company name</InputLabel>
                                        <TextField
                                            id="companyName"
                                            name="company_name"
                                            placeholder="Company name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.company_name}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Manager email</InputLabel>
                                        <TextField
                                            id="countryBasic"
                                            name="email"
                                            placeholder="Email"
                                            fullWidth
                                            autoComplete="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={type === MODAL_ACTION_TYPE.EDIT}
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                            value={values.email}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={2} sm={2}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="phone.country">
                                            Country Code
                                        </InputLabel>
                                        <CountryCodeSelect
                                            label=""
                                            size="medium"
                                            value={selectedCountry}
                                            defaultRegion={values.phone?.country || 'US'}
                                            onChange={selectedCountry => {
                                                if (!selectedCountry) return;

                                                setSelectedCountry(selectedCountry);

                                                setFieldValue(
                                                    'phone.code',
                                                    String(selectedCountry.code).replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                );

                                                setFieldValue(
                                                    'phone.country',
                                                    selectedCountry.region
                                                );
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={10} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Manager Phone</InputLabel>
                                        <TextField
                                            required
                                            id="phoneNumber"
                                            name="phone.number"
                                            inputMode="numeric"
                                            onChange={e =>
                                                setFieldValue(
                                                    'phone.number',
                                                    e.target.value.replace(/\D/g, '')
                                                )
                                            }
                                            placeholder="Phone Number"
                                            fullWidth
                                            autoComplete="tel"
                                            onBlur={handleBlur}
                                            error={
                                                touched.phone?.number &&
                                                !!errors['phone.number']
                                            }
                                            helperText={
                                                touched.phone?.number &&
                                                errors['phone.number']
                                            }
                                            value={values.phone?.number}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="role">Role</InputLabel>
                                        <Select
                                            id="roleSelect"
                                            name="type"
                                            onChange={e =>
                                                setFieldValue('type', e.target.value)
                                            }
                                            value={values.type}
                                            label="Role"
                                        >
                                            {roles.map(role => (
                                                <MenuItem key={role.key} value={role.key}>
                                                    {role.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Stack>
                                </Grid>
                                {/*{values.type === 'rental_manager' && (
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'warning.main' }}
                                        >
                                            If enabled, this manager will be able to
                                            manage all rentals within the organization.
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            <Checkbox
                                                id="primaryManager"
                                                checked={values.primary_manager}
                                                onChange={e =>
                                                    setFieldValue(
                                                        'primary_manager',
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <InputLabel htmlFor="primaryManager">
                                                Primary Manager
                                            </InputLabel>
                                        </Stack>
                                    </Grid>
                                )}*/}
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {user && (
                                        <Tooltip title="Delete Role" placement="top">
                                            <IconButton
                                                size="large"
                                                color="error"
                                                onClick={handleDelete}
                                            >
                                                <DeleteFilled />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>

                                <Grid item>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Button color="error" onClick={onClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loading}
                                            onClick={handleSubmit}
                                        >
                                            {user ? 'Edit' : 'Add'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default UpdateUserModal;
